import { StepsLayoutContainerControlled } from "components/Layout/StepsLayoutContainerControlled";
import { Requirement } from "types/requirement";
import { Species } from "types/species";
import {
  RequestPatientInfoStep,
  RequestPatientAdditionalInfoStep,
  RequestRequirementAndCoachStep,
  RequestLearnerAndRequirementStep,
  Perform,
  Reflect1,
  Submit,
  ReflectCoach,
} from "./Steps";
import { useRequestFeedback } from "./useRequestFeedback";
import { STEPS } from "./config";
import { useMemo, useEffect } from "react";
import { REQUIREMENT } from "helpers/constants";
import { VETCOIN_REWARDS } from "hooks/useVetcoin";
import { SubmissionConfirmationCard } from "components/Feedback/SubmissionConfirmationCard";
import { FeedbackVoiceAgent } from "./VoiceAgent";
import { VoiceFieldFocusProvider } from "hooks/useVoiceFieldFocus";

export const RequestFeedback = () => {
  const {
    data,
    step,
    dataFeedback,
    status,
    error,
    isCompleted,
    sites,
    activeLabel,
    tags,
    requirements,
    displayTimer,
    dispatch,
    onSubmit,
    handleStep,
    goToStepperLabel,
    onBegin,
    customLabels,
    coaches,
    learners,
    isCoachInitiated,
    historicalFeedbacks,
  } = useRequestFeedback();

  const isITER = useMemo(
    () => data?.selectedCompetencySkill?.type === REQUIREMENT.REQUIREMENT_ITER,
    [data?.selectedCompetencySkill?.type],
  );

  const steps = isCoachInitiated
    ? ["Request", "Perform", "Feedback", "Submit"]
    : ["Request", "Perform", "Reflect", "Feedback", "Submit"];

  const stepComponentList = [
    {
      label: STEPS.REQUEST2,
      component: isCoachInitiated ? (
        <RequestLearnerAndRequirementStep
          key="Step1Request2Coach"
          data={data}
          onUpdate={dispatch}
          learners={learners || []}
          competencySkills={(requirements as Requirement[]) || []}
          handleStep={handleStep}
          error={error}
        />
      ) : (
        <RequestRequirementAndCoachStep
          key="Step1Request2"
          data={data}
          onUpdate={dispatch}
          coaches={coaches || []}
          competencySkills={(requirements as Requirement[]) || []}
          handleStep={handleStep}
          error={error}
        />
      ),
    },
    ...(isITER
      ? []
      : [
          {
            label: STEPS.REQUEST,
            component: (
              <RequestPatientInfoStep
                key="Step2Request"
                onUpdate={dispatch}
                data={data}
                handleStep={handleStep}
                error={error}
                isLearnerFlow={!isCoachInitiated}
              />
            ),
          },
          {
            label: STEPS.REQUEST1,
            component: (
              <RequestPatientAdditionalInfoStep
                key="Step3Request1"
                data={data}
                onUpdate={dispatch}
                error={error}
                status={status}
                species={dataFeedback?.species as Species[]}
                sites={sites}
                handleStep={handleStep}
              />
            ),
          },
        ]),
    {
      label: STEPS.PERFORM,
      component: (
        <Perform
          key="Step2Perform"
          data={data}
          onUpdate={dispatch}
          handleStep={handleStep}
          onBegin={onBegin}
          customLabels={customLabels}
          isCoachInitiated={isCoachInitiated}
        />
      ),
    },
    ...(isCoachInitiated
      ? []
      : [
          {
            label: STEPS.REFLECT,
            component: (
              <Reflect1
                key="Step3Reflect"
                data={data}
                onUpdate={dispatch}
                handleStep={handleStep}
                error={error}
                customLabels={customLabels}
                historicalFeedbacks={historicalFeedbacks}
                allRequirements={requirements as Requirement[]}
              />
            ),
          },
        ]),
    {
      label: "ReflectCoach",
      component: (
        <ReflectCoach
          key="Step4ReflectCoach"
          data={data}
          onUpdate={dispatch}
          handleStep={handleStep}
          error={error}
          tags={tags}
          customLabels={customLabels}
          historicalFeedbacks={historicalFeedbacks}
          allRequirements={requirements as Requirement[]}
          skipLearnerReflect={isCoachInitiated}
        />
      ),
    },
    {
      label: STEPS.SUBMIT,
      component: (
        <Submit
          key="Step4Submit"
          data={data}
          onUpdate={dispatch}
          handleStep={handleStep}
          onSubmit={onSubmit}
          error={error}
          tags={tags}
          displayTimer={displayTimer}
          customLabels={customLabels}
          historicalFeedbacks={historicalFeedbacks}
          allRequirements={requirements as Requirement[]}
          hideLearnerReflect={isCoachInitiated}
        />
      ),
    },
  ];

  // Ensure the current step is valid for ITER (skip REQUEST and REQUEST1 for ITER)
  useEffect(() => {
    if (isITER && (step === STEPS.REQUEST || step === STEPS.REQUEST1)) {
      // If it's ITER and step is REQUEST or REQUEST1, redirect to PERFORM
      handleStep(STEPS.PERFORM, "next");
    }
  }, [step, isITER, handleStep]);

  if (isCompleted) {
    return (
      <SubmissionConfirmationCard
        title={
          isCoachInitiated
            ? "Coach Feedback Successfully Submitted"
            : "Case Feedback Request Successfully Submitted"
        }
        vetcoinsAwarded={
          isCoachInitiated
            ? VETCOIN_REWARDS.FEEDBACK_EVALUATION_COMPLETED
            : VETCOIN_REWARDS.FEEDBACK_REQUEST_SUBMITTED
        }
      />
    );
  }

  return (
    <VoiceFieldFocusProvider>
      <StepsLayoutContainerControlled
        steps={steps}
        StepComponentList={stepComponentList}
        activeStep={step}
        activeLabel={activeLabel}
        isCompleted={isCompleted}
        onStepClick={goToStepperLabel}
      />
      <FeedbackVoiceAgent
        step={step}
        data={data}
        dispatch={dispatch}
        handleStep={handleStep}
        onBegin={onBegin}
        isCoachInitiated={isCoachInitiated}
        coaches={coaches || []}
        learners={learners || []}
        requirements={(requirements as Requirement[]) || []}
        species={(dataFeedback?.species as Species[]) || []}
        sites={sites || []}
        tags={tags || []}
        customLabels={customLabels || []}
      />
    </VoiceFieldFocusProvider>
  );
};
